import { AppInfoLoader } from '@services/AppInfoLoader';
import { IhmSpyglassServiceClient } from '@services/coralservices/serviceClients/IhmSpyglassServiceClient';
import { StoreFamily } from '../types/StoreFamily';
import { Exception } from '../types/Exception';
import _ from 'lodash';

export class ProductInfo {
    public static supportedProductFacets = Object.freeze({
        IHM: [
            'MEDIA',
            'UNITS',
            'ASIN',
            'OFFER',
            'PRICE',
            'DESCRIPTION',
            'PLU',
            'SOURCE_TO_SELLABLE_CHILDREN',
            'SOURCE_TO_SELLABLE_PARENTS',
            'EXPERIMENTAL_OPEN_METADATA',
            'PRODUCT_ATTRIBUTES',
            'SHELF_CONSTRAINTS',
            'ITEM_DIMENSIONS',
            'ITEM_SELECTION',
            'EXTENDED_MEDIA_PACKAGE_VARIANTS',
            'PRODUCT_EXPIRATION_INFO',
            'CATEGORY',
            'INVENTORY_UNIT_OF_MEASURE',
        ],
        JANE: [
            'MEDIA',
            'UNITS',
            'ASIN',
            'OFFER',
            'PRICE',
            'DESCRIPTION',
            'PLU',
            'SOURCE_TO_SELLABLE_CHILDREN',
            'SOURCE_TO_SELLABLE_PARENTS',
            'EXPERIMENTAL_OPEN_METADATA',
            'INVENTORY_UNIT_OF_MEASURE',
            'COLOR',
        ],
        KITCHEN: [
            'MEDIA',
            'UNITS',
            'ASIN',
            'OFFER',
            'DESCRIPTION',
            'PLU',
            'SOURCE_TO_SELLABLE_CHILDREN',
            'SOURCE_TO_SELLABLE_PARENTS',
            'EXPERIMENTAL_OPEN_METADATA',
            'PRODUCT_ATTRIBUTES',
            'SHELF_CONSTRAINTS',
            'ITEM_DIMENSIONS',
            'ITEM_SELECTION',
            'EXTENDED_MEDIA_PACKAGE_VARIANTS',
            'PRODUCT_EXPIRATION_INFO',
            'CATEGORY',
        ],
        KISAN: [
            'MEDIA',
            'UNITS',
            'ASIN',
            'DESCRIPTION',
            'SOURCE_TO_SELLABLE_CHILDREN',
            'SOURCE_TO_SELLABLE_PARENTS',
            'PRODUCT_ATTRIBUTES',
            'ITEM_DIMENSIONS',
            'ITEM_SELECTION',
            'PRODUCT_EXPIRATION_INFO',
            'CATEGORY',
        ],
        KIOSK: ['MEDIA', 'UNITS', 'ASIN', 'OFFER', 'DESCRIPTION', 'PLU', 'SOURCE_TO_SELLABLE_CHILDREN', 'SOURCE_TO_SELLABLE_PARENTS', 'EXPERIMENTAL_OPEN_METADATA'],
        MENDEL: [
            'UNITS',
            'ASIN',
            'OFFER',
            'DESCRIPTION',
            'PLU',
            'SOURCE_TO_SELLABLE_CHILDREN',
            'SOURCE_TO_SELLABLE_PARENTS',
            'EXPERIMENTAL_OPEN_METADATA',
            'PRODUCT_ATTRIBUTES',
            'SHELF_CONSTRAINTS',
            'ITEM_DIMENSIONS',
            'ITEM_SELECTION',
            'PRODUCT_EXPIRATION_INFO',
            'CATEGORY',
            'MEDIA',
            'PRICE',
            'TRANSFORMATION_TARGETS',
            'TRANSFORMATION_SOURCES',
            'INVENTORY_UNIT_OF_MEASURE',
        ],
        UFF: [
            'UNITS',
            'ASIN',
            'OFFER',
            'DESCRIPTION',
            'PLU',
            'SOURCE_TO_SELLABLE_CHILDREN',
            'SOURCE_TO_SELLABLE_PARENTS',
            'EXPERIMENTAL_OPEN_METADATA',
            'PRODUCT_ATTRIBUTES',
            'SHELF_CONSTRAINTS',
            'ITEM_DIMENSIONS',
            'ITEM_SELECTION',
            'PRODUCT_EXPIRATION_INFO',
            'CATEGORY',
            'MEDIA',
            'TRANSFORMATION_TARGETS',
            'TRANSFORMATION_SOURCES',
            'INVENTORY_UNIT_OF_MEASURE',
        ],
        UV: [
            'UNITS',
            'ASIN',
            'OFFER',
            'DESCRIPTION',
            'PLU',
            'SOURCE_TO_SELLABLE_CHILDREN',
            'SOURCE_TO_SELLABLE_PARENTS',
            'EXPERIMENTAL_OPEN_METADATA',
            'PRODUCT_ATTRIBUTES',
            'SHELF_CONSTRAINTS',
            'ITEM_DIMENSIONS',
            'ITEM_SELECTION',
            'PRODUCT_EXPIRATION_INFO',
            'CATEGORY',
            'MEDIA',
            'TRANSFORMATION_TARGETS',
            'TRANSFORMATION_SOURCES',
            'INVENTORY_UNIT_OF_MEASURE',
        ],
        WFM: [
            'MEDIA',
            'UNITS',
            'ASIN',
            'DESCRIPTION',
            'PLU',
            'SOURCE_TO_SELLABLE_CHILDREN',
            'SOURCE_TO_SELLABLE_PARENTS',
            'EXPERIMENTAL_OPEN_METADATA',
            'PRODUCT_ATTRIBUTES',
            'SHELF_CONSTRAINTS',
            'ITEM_DIMENSIONS',
            'ITEM_SELECTION',
            'PRODUCT_EXPIRATION_INFO',
            'CATEGORY',
            'EXTERNAL_PRODUCT_IDENTIFIER',
            'EXTERNAL_PRODUCT_IDENTIFIERS',
        ],
        ROADIE: [
            'MEDIA',
            'UNITS',
            'ASIN',
            'OFFER',
            'DESCRIPTION',
            'PLU',
            'SOURCE_TO_SELLABLE_CHILDREN',
            'SOURCE_TO_SELLABLE_PARENTS',
            'EXPERIMENTAL_OPEN_METADATA',
            'INVENTORY_UNIT_OF_MEASURE',
            'COLOR',
        ],
        SSD: [
            'UNITS',
            'ASIN',
            'OFFER',
            'DESCRIPTION',
            'PLU',
            'SOURCE_TO_SELLABLE_CHILDREN',
            'SOURCE_TO_SELLABLE_PARENTS',
            'EXPERIMENTAL_OPEN_METADATA',
            'PRODUCT_ATTRIBUTES',
            'SHELF_CONSTRAINTS',
            'ITEM_DIMENSIONS',
            'ITEM_SELECTION',
            'PRODUCT_EXPIRATION_INFO',
            'CATEGORY',
            'MEDIA',
            'TRANSFORMATION_TARGETS',
            'TRANSFORMATION_SOURCES',
            'INVENTORY_UNIT_OF_MEASURE',
        ],
    });

    public static WFM_PLU_SCAN_CODE_TYPE = ['Scale PLU', 'POS PLU'];
    public static DEFAULT_FC_SHELF_LIFE = 10;
    public static DEFAULT_FC_SHELF_LIFE_PAD_DAY = 5;

    public static getProductInfoFacets(storeId, itemIdWithType) {
        return this.getProductInfo(storeId, itemIdWithType).then((facets) => ProductInfo.flattenFacets(facets));
    }

    public static async getProductInfo(storeId, itemIdWithType) {
        const key = { storeId, itemIdWithType };
        return this.productInfoProvider(key);
    }

    /*
        - Add productInfoProvider function logic here
     */
    public static productInfoProvider(key) {
        return this.getSupportedProductFacets(undefined).then(function (supportedProductFacets) {
            return ProductInfo.getFacets(key.storeId, key.itemIdWithType, supportedProductFacets);
        });
    }

    public static getSupportedProductFacets(providedFacets) {
        if (providedFacets && providedFacets.length > 0) {
            return providedFacets;
        }
        return AppInfoLoader.getAppConfig().then(function (appConfigData) {
            // Currently US-WI-6 is the UFF pilot store but it is coded as MENDEL
            if (appConfigData.store === 'US-WI-6') {
                return ProductInfo.supportedProductFacets.UFF;
            }
            switch (appConfigData.storeInfo.storeFamily) {
                case StoreFamily.StoreFamily.MENDEL:
                    return ProductInfo.supportedProductFacets.MENDEL;
                case StoreFamily.StoreFamily.WFM:
                    return ProductInfo.supportedProductFacets.WFM;
                case StoreFamily.StoreFamily.JANE:
                    if (appConfigData.storeInfo.isKioskStore) {
                        return ProductInfo.supportedProductFacets.KIOSK;
                    }
                    return ProductInfo.supportedProductFacets.JANE;

                case StoreFamily.StoreFamily.IHM:
                    if (appConfigData.storeInfo.isKitchen) {
                        return ProductInfo.supportedProductFacets.KITCHEN;
                    }
                    return ProductInfo.supportedProductFacets.IHM;
                case StoreFamily.StoreFamily.KISAN:
                    return ProductInfo.supportedProductFacets.KISAN;
                case StoreFamily.StoreFamily.UV:
                    return ProductInfo.supportedProductFacets.UV;
                case StoreFamily.StoreFamily.ROADIE:
                    return ProductInfo.supportedProductFacets.ROADIE;
                case StoreFamily.StoreFamily.HOUDINI:
                    return ProductInfo.supportedProductFacets.UFF;
                case StoreFamily.StoreFamily.SSD:
                    return ProductInfo.supportedProductFacets.SSD;
                default:
                    console.log('Using default product facets, unrecognized store family: ', appConfigData.storeInfo.storeFamily);
                    return ProductInfo.supportedProductFacets.JANE;
            }
        });
    }

    public static getFacets(storeId, itemIdWithType, facetNames, marketplaceId?, merchantId?) {
        return IhmSpyglassServiceClient.getProductInfo(storeId, itemIdWithType, facetNames, marketplaceId, merchantId)
            .then(function (resolvedData) {
                return resolvedData.facets;
            })
            .catch(function (error) {
                let errorMessage = 'Failed to load product info for: ' + itemIdWithType.itemId + '.';
                if (error.getMessage) {
                    errorMessage = errorMessage + ' Reason: ' + error.getMessage;
                }
                return Promise.reject(
                    new Exception({
                        message: errorMessage,
                        cause: error,
                    }),
                );
            });
    }

    public static scanCodeTypeIsPluInWfm(facets) {
        return (
            facets.EXTERNAL_PRODUCT_IDENTIFIER &&
            facets.EXTERNAL_PRODUCT_IDENTIFIER.identifiers &&
            facets.EXTERNAL_PRODUCT_IDENTIFIER.identifiers.ScanCodeType &&
            this.WFM_PLU_SCAN_CODE_TYPE.indexOf(facets.EXTERNAL_PRODUCT_IDENTIFIER.identifiers.ScanCodeType.value) >= 0
        );
    }
    public static pluExistsInWfm(facets) {
        return this.scanCodeTypeIsPluInWfm(facets) && facets.EXTERNAL_PRODUCT_IDENTIFIER.identifiers.ScanCode && facets.EXTERNAL_PRODUCT_IDENTIFIER.identifiers.ScanCode.value;
    }

    public static replaceUnderscoresWithSpaces(str) {
        return str.replace(/_/g, ' ');
    }

    public static flattenFacets(facets) {
        let asin = '';
        let plu = '';
        let title = '';
        let binding = '';
        let authors;
        let brand;
        let shortName;
        let animalProductionMethod;
        let color;
        let standardizedColors = [];
        let genericKeywords = [];
        let publicationDate;
        let imageUrl = '';
        let inventoryUnitOfMeasure = '';
        let unitPriceWithUnit = {};
        let unitPrice = {};
        let previouslyPublishedUnitPriceWithUnit = {};
        let previouslyPublishedUnitPrice = {};
        let regularChargeList = [];
        let primeChargeList = [];
        let prevPublishedPriceGranite;
        let isBuyable;
        let isRecalled;
        let releaseDate;
        let soldByUnit = '';
        let weight = {};
        let children = [];
        let parents = [];
        let autoFacingConstraints = {};
        let prepCategory = '';
        let itemWidthInMillimeters = 0;
        let unitCount = {};
        let prepInstruction;
        let packageVariation;
        let andonCord;
        let manufacturer = '';
        let productImages = [];
        let glProductGroupType = '';
        let upc;
        let fcShelfLife = this.DEFAULT_FC_SHELF_LIFE;
        let fcShelfLifePadDay = this.DEFAULT_FC_SHELF_LIFE_PAD_DAY;
        let isExpirationDatedProduct;
        let productExpirationType;
        const shouldAutoGenExpDate = false;
        let externalProductIdentifiers = {};
        let transformationDefinitionTargets = [];
        let transformationDefinitionSources = [];
        let externalProductIdentifierMaps = {};

        return AppInfoLoader.getAppConfig().then(function (value) {
            const storeFamily = value.storeInfo.storeFamily;
            if (facets.EXTERNAL_PRODUCT_IDENTIFIER && facets.EXTERNAL_PRODUCT_IDENTIFIER.identifiers) {
                externalProductIdentifiers = facets.EXTERNAL_PRODUCT_IDENTIFIER.identifiers;
            }

            if (facets.EXTERNAL_PRODUCT_IDENTIFIERS && facets.EXTERNAL_PRODUCT_IDENTIFIERS.identifiers) {
                externalProductIdentifierMaps = facets.EXTERNAL_PRODUCT_IDENTIFIERS.identifiers;
            }

            if (facets.ASIN && facets.ASIN.asin) {
                asin = facets.ASIN.asin;
            }
            if (storeFamily === StoreFamily.StoreFamily.WFM && ProductInfo.pluExistsInWfm(facets)) {
                plu = facets.EXTERNAL_PRODUCT_IDENTIFIER.identifiers.ScanCode.value;
            } else if (facets.PLU && facets.PLU.plu) {
                plu = facets.PLU.plu;
            }

            if (facets.COLOR) {
                if (facets.COLOR.color) {
                    color = facets.COLOR.color;
                }
                if (facets.COLOR.standardizedColors) {
                    standardizedColors = facets.COLOR.standardizedColors;
                }
            }

            if (facets.DESCRIPTION) {
                if (facets.DESCRIPTION.title) {
                    title = facets.DESCRIPTION.title;
                }
                if (facets.DESCRIPTION.binding) {
                    binding = ProductInfo.replaceUnderscoresWithSpaces(facets.DESCRIPTION.binding);
                }
                if (facets.DESCRIPTION.brand) {
                    brand = facets.DESCRIPTION.brand;
                }
                if (facets.DESCRIPTION.authors) {
                    authors = facets.DESCRIPTION.authors;
                }
                if (facets.DESCRIPTION.genericKeywords) {
                    genericKeywords = facets.DESCRIPTION.genericKeywords;
                }
                if (facets.DESCRIPTION.publicationDate) {
                    publicationDate = facets.DESCRIPTION.publicationDate;
                }
                if (facets.DESCRIPTION.shortName) {
                    shortName = facets.DESCRIPTION.shortName;
                }
                if (facets.DESCRIPTION.animalProductionMethod) {
                    animalProductionMethod = facets.DESCRIPTION.animalProductionMethod;
                }
            }

            if (facets.PRICE) {
                if (facets.PRICE.regularChargeList) {
                    regularChargeList = facets.PRICE.regularChargeList;
                }
                if (facets.PRICE.primeChargeList) {
                    primeChargeList = facets.PRICE.primeChargeList;
                }
                if (facets.PRICE.prevPublishedPrice) {
                    prevPublishedPriceGranite = facets.PRICE.prevPublishedPrice.value;
                }
            }
            if (facets.MEDIA && facets.MEDIA.productImage && facets.MEDIA.productImage.url) {
                imageUrl = facets.MEDIA.productImage.url;
            }
            if (facets.MEDIA_NON_JIHM && facets.MEDIA_NON_JIHM.productImage && facets.MEDIA_NON_JIHM.productImage.url) {
                imageUrl = facets.MEDIA_NON_JIHM.productImage.url;
            }
            if (facets.OFFER) {
                if (facets.OFFER.unitPrice) {
                    unitPriceWithUnit = facets.OFFER.unitPrice;
                    if (facets.OFFER.unitPrice.price) {
                        unitPrice = facets.OFFER.unitPrice.price;
                    }
                }
                if (facets.OFFER.previouslyPublishedUnitPrice) {
                    previouslyPublishedUnitPriceWithUnit = facets.OFFER.previouslyPublishedUnitPrice;
                    if (facets.OFFER.previouslyPublishedUnitPrice.price) {
                        previouslyPublishedUnitPrice = facets.OFFER.previouslyPublishedUnitPrice.price;
                    }
                }
                if (facets.OFFER.isBuyable) {
                    isBuyable = facets.OFFER.isBuyable;
                }
                if (facets.OFFER.recalled) {
                    isRecalled = facets.OFFER.recalled;
                }
                if (facets.OFFER.releaseDate) {
                    releaseDate = new Date(facets.OFFER.releaseDate * 1000);
                }
            }
            if (facets.UNITS) {
                if (facets.UNITS.soldByUnit) {
                    soldByUnit = facets.UNITS.soldByUnit;
                }
                if (facets.UNITS.weight) {
                    weight = facets.UNITS.weight;
                }
            }
            if (facets.SOURCE_TO_SELLABLE_CHILDREN) {
                children = facets.SOURCE_TO_SELLABLE_CHILDREN.children;
            }
            if (facets.SOURCE_TO_SELLABLE_PARENTS) {
                parents = facets.SOURCE_TO_SELLABLE_PARENTS.parents;
            }

            if (facets.SHELF_CONSTRAINTS && facets.SHELF_CONSTRAINTS.shelfContraints && facets.SHELF_CONSTRAINTS.shelfContraints.AUTO_FACING) {
                autoFacingConstraints = facets.SHELF_CONSTRAINTS.shelfContraints.AUTO_FACING;
            }

            if (facets.CATEGORY && facets.CATEGORY.prepCategory) {
                // 3P HMR should be treated the same as 3P for associate tools
                prepCategory = '3PHMR' === facets.CATEGORY.prepCategory ? '3P' : facets.CATEGORY.prepCategory;
            }

            if (facets.EXPERIMENTAL_OPEN_METADATA && facets.EXPERIMENTAL_OPEN_METADATA.glProductGroupType) {
                glProductGroupType = facets.EXPERIMENTAL_OPEN_METADATA.glProductGroupType;
            }

            if (facets.ITEM_DIMENSIONS && facets.ITEM_DIMENSIONS.widthInMillimeters) {
                itemWidthInMillimeters = facets.ITEM_DIMENSIONS.widthInMillimeters;
            }

            if (facets.PRODUCT_ATTRIBUTES && facets.PRODUCT_ATTRIBUTES.unitCount) {
                unitCount = facets.PRODUCT_ATTRIBUTES.unitCount;
            }

            if (facets.PRODUCT_ATTRIBUTES && facets.PRODUCT_ATTRIBUTES.upc) {
                upc = facets.PRODUCT_ATTRIBUTES.upc;
            }

            if (facets.ITEM_SELECTION && facets.ITEM_SELECTION.prepInstruction) {
                prepInstruction = facets.ITEM_SELECTION.prepInstruction;
            }

            if (facets.ITEM_SELECTION && facets.ITEM_SELECTION.packageVariation) {
                packageVariation = facets.ITEM_SELECTION.packageVariation;
            }

            if (facets.ITEM_SELECTION && facets.ITEM_SELECTION.andonCord) {
                andonCord = facets.ITEM_SELECTION.andonCord;
            }

            if (facets.PRODUCT_ATTRIBUTES && facets.PRODUCT_ATTRIBUTES.manufacturer) {
                manufacturer = facets.PRODUCT_ATTRIBUTES.manufacturer;
            }

            if (facets.EXTENDED_MEDIA_PACKAGE_VARIANTS && facets.EXTENDED_MEDIA_PACKAGE_VARIANTS.productImages) {
                productImages = facets.EXTENDED_MEDIA_PACKAGE_VARIANTS.productImages;
            }

            if (facets.PRODUCT_EXPIRATION_INFO && _.isFinite(facets.PRODUCT_EXPIRATION_INFO.fcShelfLife)) {
                fcShelfLife = facets.PRODUCT_EXPIRATION_INFO.fcShelfLife;
            }

            if (facets.PRODUCT_EXPIRATION_INFO && _.isFinite(facets.PRODUCT_EXPIRATION_INFO.fcShelfLifePadDay)) {
                fcShelfLifePadDay = facets.PRODUCT_EXPIRATION_INFO.fcShelfLifePadDay;
            }

            // missingcatalogdata is a valid use case,so we need to propagate the difference between false and undefined
            if (facets.PRODUCT_EXPIRATION_INFO) {
                isExpirationDatedProduct = facets.PRODUCT_EXPIRATION_INFO.isExpirationDatedProduct;
            }

            if (facets.PRODUCT_EXPIRATION_INFO && facets.PRODUCT_EXPIRATION_INFO.productExpirationType) {
                productExpirationType = facets.PRODUCT_EXPIRATION_INFO.productExpirationType;
            }

            if (facets.TRANSFORMATION_TARGETS && facets.TRANSFORMATION_TARGETS.transformationDefinitionTargets) {
                transformationDefinitionTargets = facets.TRANSFORMATION_TARGETS.transformationDefinitionTargets;
            }

            if (facets.TRANSFORMATION_SOURCES && facets.TRANSFORMATION_SOURCES.transformationDefinitionSources) {
                transformationDefinitionSources = facets.TRANSFORMATION_SOURCES.transformationDefinitionSources;
            }

            if (facets.INVENTORY_UNIT_OF_MEASURE && facets.INVENTORY_UNIT_OF_MEASURE.inventoryUnitOfMeasure) {
                inventoryUnitOfMeasure = facets.INVENTORY_UNIT_OF_MEASURE.inventoryUnitOfMeasure;
            }

            return {
                asin,
                plu,
                title,
                binding,
                authors,
                brand,
                color,
                shortName,
                animalProductionMethod,
                standardizedColors,
                genericKeywords,
                imageUrl,
                unitPriceWithUnit,
                unitPrice,
                previouslyPublishedUnitPriceWithUnit,
                previouslyPublishedUnitPrice,
                regularChargeList,
                prevPublishedPriceGranite,
                primeChargeList,
                isBuyable,
                isRecalled,
                releaseDate,
                soldByUnit,
                weight,
                children,
                parents,
                autoFacingConstraints,
                prepCategory,
                glProductGroupType,
                itemWidthInMillimeters,
                unitCount,
                prepInstruction,
                packageVariation,
                andonCord,
                manufacturer,
                productImages,
                upc,
                fcShelfLife,
                fcShelfLifePadDay,
                isExpirationDatedProduct,
                productExpirationType,
                shouldAutoGenExpDate,
                externalProductIdentifiers,
                transformationDefinitionTargets,
                transformationDefinitionSources,
                externalProductIdentifierMaps,
                inventoryUnitOfMeasure,
                publicationDate,
            };
        });
    }
}
